import apiClient from '@/utils/request'
import { nestedQueryFormatter } from '@/utils/query'
import {
  IAdScansPayload,
  IAdScansResponse,
  IAdScanPayload,
  IDetectionsResponse,
  IScopesResponse
} from '@/interfaces/ad_scans'

// BASE_API is .../api/v1/ — step up to v2 (avoid /api/v1/api/v2/...)
const detectionOverridesPath = '../v2/traforama/detection_overrides'

export const fetchItems = async (
  payload: IAdScansPayload
): Promise<IAdScansResponse> => {
  const params = Object.assign(
    { limit: payload.limit, start: payload.start },
    nestedQueryFormatter('filter', payload.filter),
    nestedQueryFormatter('sorter', { direction: 'desc', field: 'created_at' })
  )
  const response = await apiClient.get<IAdScansResponse>('/ad_scans', {
    params: params
  })
  return response.data
}

export const createItem = async (
  payload: IAdScanPayload
): Promise<IAdScansResponse> => {
  const response = await apiClient.post<IAdScansResponse>('/ad_scans', payload)
  return response.data
}

const normalizeIso = (iso?: string | null): string | null =>
  iso ? iso.trim().toUpperCase() || null : null

const sourceablePath = (type: string, id: string) =>
  `${encodeURIComponent(type)}/${encodeURIComponent(id)}`

export const fetchDetections = async (params: {
  sourceable_type: string
  sourceable_id: string
  iso?: string | null
}): Promise<IDetectionsResponse> => {
  const iso = normalizeIso(params.iso)
  const base = `${detectionOverridesPath}/catalog/${sourceablePath(
    params.sourceable_type,
    params.sourceable_id
  )}`
  const url = iso ? `${base}/${encodeURIComponent(iso)}` : base
  const response = await apiClient.get(url)
  const body = response.data as any
  return body.data ?? body
}

export const fetchScopes = async (params: {
  sourceable_type: string
  sourceable_id: string
}): Promise<IScopesResponse> => {
  const url = `${detectionOverridesPath}/scopes/${sourceablePath(
    params.sourceable_type,
    params.sourceable_id
  )}`
  const response = await apiClient.get(url)
  const body = response.data as any
  return body.data ?? body
}

export const fetchDetectionOverrides = async (params: {
  sourceable_type: string
  sourceable_id: string
}) => {
  const response = await apiClient.get(detectionOverridesPath, {
    params: nestedQueryFormatter('filter', {
      sourceable_type: params.sourceable_type,
      sourceable_id: params.sourceable_id
    })
  })
  return response.data
}

export const createDetectionOverride = async (payload: {
  sourceable_type: string
  sourceable_id: string
  driver: string
  detections: string[]
  iso?: string | null
}) => {
  const response = await apiClient.post(detectionOverridesPath, {
    ...payload,
    iso: normalizeIso(payload.iso)
  })
  return response.data
}

export const updateDetectionOverride = async (payload: {
  id: number
  detections: string[]
}) => {
  const response = await apiClient.put(
    `${detectionOverridesPath}/${payload.id}`,
    { detections: payload.detections }
  )
  return response.data
}

export const destroyDetectionOverride = async (id: number) => {
  const response = await apiClient.delete(`${detectionOverridesPath}/${id}`)
  return response.data
}

export const updateDetections = async (payload: {
  sourceable_type: string
  sourceable_id: string
  detections: Record<string, string[]>
  iso?: string | null
}): Promise<IDetectionsResponse> => {
  const iso = normalizeIso(payload.iso)
  const listResponse = await fetchDetectionOverrides({
    sourceable_type: payload.sourceable_type,
    sourceable_id: payload.sourceable_id
  })
  const existing = (listResponse.data ?? []) as Array<{
    id: number
    driver: string
    iso: string | null
  }>

  // Persist the exact checkbox state. An empty list is a deliberate "clear this
  // scope" (full replacement), stored as an empty override — not a delete — so
  // e.g. Global can be emptied while per-country overrides stay in effect.
  const upsertDriver = async (driver: string, detections: string[]) => {
    const record = existing.find(
      (item) => item.driver === driver && normalizeIso(item.iso) === iso
    )

    if (record) {
      await updateDetectionOverride({ id: record.id, detections })
    } else {
      await createDetectionOverride({
        sourceable_type: payload.sourceable_type,
        sourceable_id: payload.sourceable_id,
        driver,
        detections,
        iso
      })
    }
  }

  for (const [driver, detections] of Object.entries(payload.detections)) {
    await upsertDriver(driver, detections)
  }

  return fetchDetections({
    sourceable_type: payload.sourceable_type,
    sourceable_id: payload.sourceable_id,
    iso
  })
}
